type NewRelicCustomAttributes = {
  accountId?: string;
  appId?: string;
  eventType?: string;
  [key: string]: unknown;
};

/**
 * Composable for NewRelic browser agent functionality
 * Provides a type-safe interface to NewRelic methods
 */
export function useNewRelic() {
  const newRelic = window.newrelic;

  /**
   * Report an error to NewRelic
   * @param error - The error to report
   * @param customAttributes - Additional attributes to include with the error
   * accountId, appId, eventType are NewRelic keywords and should be mapped accordingly
   */
  const noticeError = (error: Error | string, customAttributes?: NewRelicCustomAttributes) => {
    newRelic.noticeError(error, {
      ...customAttributes,
      ...(customAttributes?.accountId && { accountNumber: customAttributes.accountId }),
      ...(customAttributes?.appId && { app_id: customAttributes.appId }),
      ...(customAttributes?.eventType && { event_type: customAttributes.eventType }),
    });
  };

  /**
   * Record a custom PageAction event in NewRelic
   * @param name - The action name (appears as actionName attribute)
   * @param attributes - Key/value pairs attached to the event
   */
  const addPageAction = (name: string, attributes?: Record<string, unknown>) => {
    newRelic?.addPageAction(name, attributes);
  };

  /**
   * Send a structured log entry to NewRelic
   * @param message - The log message
   * @param options - Optional level and custom attributes
   */
  const log = (
    message: string,
    options?: { customAttributes?: Record<string, unknown>; level?: 'ERROR' | 'TRACE' | 'DEBUG' | 'INFO' | 'WARN' },
  ) => {
    newRelic?.log(message, options);
  };

  return {
    noticeError,
    addPageAction,
    log,
  };
}
